import {EApiSourceName} from '../redux/types';
import {IChainData} from './types';
import {getChainData} from './utilities';

export enum ChainId {
  UNSET = 0,
  ETH_MAINNET = 1,
  BINANCE_MAINNET = 56,
  BINANCE_TESTNET = 97,
  CSC_MAINNET = 52,
  CSC_TESTNET = 53,
  // OKC_MAINNET = 66,
  // OKC_TESTNET = 65,
  POLYGON_MAINNET = 137,
  POLYGON_TESTNET = 80001,
  HARDHAT = 31337,
}

export enum EChainName {
  ETH = 'ETH',
  BINANCE = 'BNB',
  POLYGON = 'MATIC',
  HARDHAT = 'HARDHAT',
  BINANCE_TESTNET = 'BNB_TESTNET',
  POLYGON_TESTNET = 'MATIC_TESTNET',
}

export const chainIdToNetworkName: Record<number, string> = {
  [ChainId.BINANCE_MAINNET]: EChainName.BINANCE,
  [ChainId.POLYGON_MAINNET]: EChainName.POLYGON,
  [ChainId.BINANCE_TESTNET]: EChainName.BINANCE_TESTNET,
  [ChainId.POLYGON_TESTNET]: EChainName.POLYGON_TESTNET,
  [ChainId.HARDHAT]: EChainName.HARDHAT,
  [ChainId.ETH_MAINNET]: EChainName.ETH,
};

export const contractNameToAddress: Partial<Record<EApiSourceName, (chainId: number) => string>> = {
  [EApiSourceName.SOLUTION]: (chainId: number) => process.env[`REACT_APP_${chainIdToNetworkName[chainId]}_ROOT`] || '',
  [EApiSourceName.GFT_ROOT]: (chainId: number) => process.env[`REACT_APP_${chainIdToNetworkName[chainId]}_ROOT`] || '',
};

export const setupNetwork = async (chainId: ChainId, provider?: any) => {
  let innerProvider = window.ethereum;
  if (provider) {
    innerProvider = provider;
  }

  let isError: null | boolean = null;

  const chainData = getChainData(chainId);
  if (!chainData || chainData instanceof Error) {
    return (isError = true);
  }

  try {
    isError = await innerProvider.request({
      method: 'wallet_addEthereumChain',
      params: [
        {
          chainId: `0x${chainId.toString(16)}`,
          chainName: chainData.name,
          nativeCurrency: chainData.native_currency,
          rpcUrls: chainData.rpc_url,
          blockExplorerUrls: chainData.block_explorer_urls,
        },
      ],
    });
  } catch (addError) {
    console.error('Failed to setup the network:', addError);
    isError = true;
  }

  return isError;
};

export const switchNetwork = async (chainId: ChainId, provider?: any) => {
  let innerProvider = window.ethereum;
  if (provider) {
    innerProvider = provider;
  }

  if (!innerProvider) {
    console.error("Can't setup the BSC network on metamask because window.ethereum is undefined");
    return false;
  }

  let isError = null;

  const ethereum = Object.assign({}, window.ethereum);
  if (ethereum.isTrust) return isError;
  console.log('chainId.toString(16)', chainId, chainId.toString(16));
  try {
    isError = await innerProvider.request({
      method: 'wallet_switchEthereumChain',
      params: [{chainId: `0x${chainId.toString(16)}`}],
    });
  } catch (switchError: any) {
    isError = true;
    console.error('Failed to switch the network:', switchError);

    if (switchError.code === 4902 || switchError?.data?.originalError?.code === 4902) {
      isError = await setupNetwork(chainId, innerProvider);
    }
  }
  return isError;
};
// export const setupNetwork = async () => {
//   const provider = window.ethereum;
//   if (provider) {
//     const chainId = PROD_CHAIN_ID;
//     try {
//       await provider.request({
//         method: "wallet_addEthereumChain",
//         params: [
//           {
//             chainId: `0x${chainId.toString(16)}`,
//             chainName: "Binance Smart Chain Mainnet",
//             nativeCurrency: {
//               name: "BNB",
//               symbol: "bnb",
//               decimals: 18,
//             },
//             rpcUrls: ["https://bsc-dataseed.binance.org/"],
//             blockExplorerUrls: ["https://bscscan.com/"],
//           },
//         ],
//       });
//       return true;
//     } catch (error) {
//       console.error("Failed to setup the network in Metamask:", error);
//       return false;
//     }
//   } else {
//     console.error(
//       "Can't setup the BSC network on metamask because window.ethereum is undefined"
//     );
//     return false;
//   }
// };

export const supportedChainsProduction: IChainData[] = [
  {
    name: 'Binance Smart Chain',
    short_name: 'bsc',
    chain: 'smartchain',
    network: 'mainnet',
    chain_id: 56,
    network_id: 56,
    rpc_url: 'https://bsc-dataseed1.defibit.io/',
    native_currency: {
      symbol: 'BNB',
      name: 'BNB',
      decimals: '18',
    },
    block_explorer_urls: ['https://bscscan.com/'],
  },
  {
    name: 'CoinEx Smart Chain Mainnet',
    short_name: 'cet',
    chain: 'CSC',
    network: 'mainnet',
    chain_id: 52,
    network_id: 52,
    rpc_url: 'https://rpc.coinex.net',
    native_currency: {
      name: 'CoinEx Chain Native Token',
      symbol: 'cet',
      decimals: '18',
    },
    block_explorer_urls: ['https://www.coinex.net/'],
  },
];

const supportedChains: IChainData[] = [
  {
    name: 'Ethereum Mainnet',
    short_name: 'eth',
    chain: 'ETH',
    network: 'mainnet',
    chain_id: 1,
    network_id: 1,
    rpc_url: 'https://mainnet.infura.io/v3/%API_KEY%',
    native_currency: {
      symbol: 'ETH',
      name: 'Ethereum',
      decimals: '18',
    },
  },
  {
    name: 'Ethereum Ropsten',
    short_name: 'rop',
    chain: 'ETH',
    network: 'ropsten',
    chain_id: 3,
    network_id: 3,
    rpc_url: 'https://ropsten.infura.io/v3/%API_KEY%',
    native_currency: {
      symbol: 'ETH',
      name: 'Ethereum',
      decimals: '18',
    },
  },
  {
    name: 'Ethereum Rinkeby',
    short_name: 'rin',
    chain: 'ETH',
    network: 'rinkeby',
    chain_id: 4,
    network_id: 4,
    rpc_url: 'https://rinkeby.infura.io/v3/%API_KEY%',
    native_currency: {
      symbol: 'ETH',
      name: 'Ethereum',
      decimals: '18',
    },
  },
  {
    name: 'Ethereum Görli',
    short_name: 'gor',
    chain: 'ETH',
    network: 'goerli',
    chain_id: 5,
    network_id: 5,
    rpc_url: 'https://goerli.infura.io/v3/%API_KEY%',
    native_currency: {
      symbol: 'ETH',
      name: 'Ethereum',
      decimals: '18',
    },
  },
  {
    name: 'RSK Mainnet',
    short_name: 'rsk',
    chain: 'RSK',
    network: 'mainnet',
    chain_id: 30,
    network_id: 30,
    rpc_url: 'https://public-node.rsk.co',
    native_currency: {
      symbol: 'RSK',
      name: 'RSK',
      decimals: '18',
    },
  },
  {
    name: 'Ethereum Kovan',
    short_name: 'kov',
    chain: 'ETH',
    network: 'kovan',
    chain_id: 42,
    network_id: 42,
    rpc_url: 'https://kovan.infura.io/v3/%API_KEY%',
    native_currency: {
      symbol: 'ETH',
      name: 'Ethereum',
      decimals: '18',
    },
  },
  {
    name: 'Ethereum Classic Mainnet',
    short_name: 'etc',
    chain: 'ETC',
    network: 'mainnet',
    chain_id: 61,
    network_id: 1,
    rpc_url: 'https://ethereumclassic.network',
    native_currency: {
      symbol: 'ETH',
      name: 'Ethereum',
      decimals: '18',
    },
  },
  {
    name: 'POA Network Sokol',
    short_name: 'poa',
    chain: 'POA',
    network: 'sokol',
    chain_id: 77,
    network_id: 77,
    rpc_url: 'https://sokol.poa.network',
    native_currency: {
      symbol: 'POA',
      name: 'POA',
      decimals: '18',
    },
  },
  {
    name: 'POA Network Core',
    short_name: 'skl',
    chain: 'POA',
    network: 'core',
    chain_id: 99,
    network_id: 99,
    rpc_url: 'https://core.poa.network',
    native_currency: {
      symbol: 'POA',
      name: 'POA',
      decimals: '18',
    },
  },
  {
    name: 'xDAI Chain',
    short_name: 'xdai',
    chain: 'POA',
    network: 'dai',
    chain_id: 100,
    network_id: 100,
    rpc_url: 'https://dai.poa.network',
    native_currency: {
      symbol: 'xDAI',
      name: 'xDAI',
      decimals: '18',
    },
  },
  {
    name: 'Callisto Mainnet',
    short_name: 'clo',
    chain: 'callisto',
    network: 'mainnet',
    chain_id: 820,
    network_id: 1,
    rpc_url: 'https://clo-geth.0xinfra.com/',
    native_currency: {
      symbol: 'CLO',
      name: 'CLO',
      decimals: '18',
    },
  },
  {
    name: 'Binance Smart Chain',
    short_name: 'bsc',
    chain: 'smartchain',
    network: 'mainnet',
    chain_id: 56,
    network_id: 56,
    rpc_url: 'https://bsc-dataseed1.defibit.io/',
    native_currency: {
      symbol: 'BNB',
      name: 'BNB',
      decimals: '18',
    },
  },
  {
    name: 'Hardhat',
    short_name: 'hdh',
    chain: 'ETH',
    network: 'hardhat',
    chain_id: 31337,
    network_id: 31337,
    rpc_url: 'http://localhost:8545/',
    native_currency: {
      symbol: 'ETH',
      name: 'ETH',
      decimals: '18',
    },
  },
  {
    name: 'BSC Testnet',
    short_name: 'bsc',
    chain: 'smartchain',
    network: 'testnet',
    chain_id: 97,
    network_id: 97,
    rpc_url: 'https://speedy-nodes-nyc.moralis.io/f46b513ed525fb4e771dc9ce/bsc/testnet',
    native_currency: {
      symbol: 'BNB',
      name: 'BNB',
      decimals: '18',
    },
  },
  {
    name: 'CoinEx Smart Chain Mainnet',
    short_name: 'cet',
    chain: 'CSC',
    network: 'mainnet',
    chain_id: 52,
    network_id: 52,
    rpc_url: 'https://rpc.coinex.net',
    native_currency: {
      name: 'CoinEx Chain Native Token',
      symbol: 'cet',
      decimals: '18',
    },
    block_explorer_urls: ['https://www.coinex.net/'],
  },
  {
    name: 'CoinEx Smart Chain Testnet',
    short_name: 'tcet',
    chain: 'CSC',
    network: 'testnet',
    chain_id: 53,
    network_id: 53,
    rpc_url: 'https://testnet-rpc.coinex.net/',
    native_currency: {
      name: 'CoinEx Chain Test Native Token',
      symbol: 'cet',
      decimals: '18',
    },
    block_explorer_urls: ['https://testnet.coinex.net/'],
  },
];

const getSupportedChains = () =>
  process.env.REACT_APP_ENVIRONMENT === 'production' ? supportedChainsProduction : supportedChains;

export default getSupportedChains;
